import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import kameshImage from '../../assets/images/profiles/kamesh_upadhya.jpg';
import './AboutUs.css';
import SEOHead from '../SEOHead';

const AboutUs: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Immediate scroll to top on component render
  React.useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [selectedTeamMember, setSelectedTeamMember] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const teamMembers = [
    {
      id: 1,
      name: "Dr Kameshwar Upadhyay",
      title: "Antaryatra Mentor",
      shortDescription: "25+ years experience in Vedic rituals and Sanskrit scriptures.",
      fullDescription: "Dr Kameshwar Upadhyay is a highly respected Vedic scholar with over 25 years of dedicated service to spiritual traditions. He completed his advanced studies in Sanskrit and Vedic philosophy from Banaras Hindu University. His expertise spans across complex rituals including Maha Mrityunjaya, Rudrabhishekh, and Graha Shanti ceremonies.",
      expertise: ["Vedic Rituals", "Sanskrit Scholar", "Maha Mrityunjaya", "Rudrabhishekh"],
      image: kameshImage,
      education: "PhD Astrology, Sanskrit, Banaras Hindu University",
      specialization: "Complex Vedic ceremonies and Astrology expertise",
      languages: ["Hindi", "Sanskrit", "English"],
      achievements: [
        "1000+ successful ceremonies performed",
        "Expert in ancient Vedic traditions",
        "Spiritual advisor to numerous families",
        "Published researcher in Vedic studies"
      ]
    },
    {
      id: 3,
      name: "Dr. Satendra Mishra",
      title: "Spiritual Counselor & Astrologer",
      shortDescription: "PhD in Ancient Philosophy with expertise in spiritual guidance.",
      fullDescription: "Dr. Satendra Mishra combines academic excellence with deep spiritual insight. His PhD in Ancient Philosophy, coupled with traditional astrological training, enables him to provide comprehensive spiritual guidance. He specializes in Vedic astrology, meditation techniques, and personalized spiritual counseling.",
      expertise: ["Astrology", "Spiritual Counseling", "Meditation", "Vedic Philosophy"],
      image: "/images/team/astrologer-devi.jpg",
      education: "PhD Ancient Philosophy, M.A. Vedic Astrology",
      specialization: "Astrological consultations and spiritual counseling",
      languages: ["Hindi", "English", "Sanskrit"],
      achievements: [
        "1000+ astrological consultations",
        "PhD in Ancient Philosophy",
        "Published author on Vedic wisdom",
        "International spiritual speaker"
      ]
    },
    {
      id: 4,
      name: "Pandit Vikram Singh",
      title: "Vishnu Sahasranama Expert",
      shortDescription: "Dedicated practitioner with melodious chanting abilities.",
      fullDescription: "Pandit Vikram Singh is renowned for his exceptional devotion to Lord Vishnu and his masterful recitation of Vishnu Sahasranama. His melodious voice and deep spiritual connection create a transcendent experience during ceremonies. He has dedicated his life to preserving and sharing the divine vibrations of Vishnu worship.",
      expertise: ["Vishnu Worship", "Devotional Singing", "Sahasranama", "Temple Traditions"],
      image: "/images/team/pandit-singh.jpg",
      education: "Traditional Vedic Training, Music in Devotional Arts",
      specialization: "Vishnu Sahasranama and devotional ceremonies",
      languages: ["Hindi", "Sanskrit", "Punjabi"],
      achievements: [
        "Master of Vishnu Sahasranama",
        "Melodious devotional singer",
        "Temple ceremony expert",
        "Devotional music composer"
      ]
    }
  ];

  const handleTeamMemberClick = (member: any) => {
    setSelectedTeamMember(member);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTeamMember(null);
  };

  const handleExploreServices = () => {
    navigate('/', { state: { scrollToServices: true } });
  };

  const testimonials = [
    {
      text: "The Maha Mrityunjaya Pujan performed by Pandit Sharma brought incredible peace to our family during a difficult time. The authenticity and devotion were evident throughout the ceremony.",
      author: "Rashmi Patel",
      location: "Mumbai, India",
      image: "/images/testimonials/rashmi-patel.jpg"
    },
    {
      text: "Living abroad, we thought we'd lost connection to our spiritual roots. ProjectX brought the sacred traditions right to our home. Truly blessed!",
      author: "Sanjay Kumar", 
      location: "California, USA",
      image: "/images/testimonials/sanjay-kumar.jpg"
    },
    {
      text: "The spiritual guidance and regular pujas have transformed our home's energy completely. Our children are now more connected to their heritage.",
      author: "Priya Sharma",
      location: "Toronto, Canada", 
      image: "/images/testimonials/priya-sharma.jpg"
    }
  ];

  return (
    <div className="about-us">
      <SEOHead
        title="About Us – Connecting Families with Authentic Vedic Traditions"
        description="Learn about Antaryatra – founded to simplify sacred Vedic rituals. Meet our team of verified Kashi pandits and scholars dedicated to authentic puja ceremonies."
        canonicalPath="/about"
        keywords="about Antaryatra, Kashi Acharyas, Vedic scholars, puja platform India, Kashi certified pandit"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "About Antaryatra",
          "url": "https://www.antaryatra.org/about",
          "description": "Antaryatra connects families with Kashi-certified Acharyas for authentic Vedic puja at home in Delhi NCR or at our sacred Kashi site.",
          "mainEntity": {
            "@type": "Organization",
            "name": "Antaryatra",
            "url": "https://www.antaryatra.org",
            "logo": "https://www.antaryatra.org/logo512.png",
            "foundingLocation": { "@type": "Place", "name": "Varanasi, India" },
            "sameAs": ["https://www.youtube.com/@antaryatra", "https://www.instagram.com/antaryatra"]
          }
        }}
      />
      {/* Compact Hero Section with Vision & Mission */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content">
            <div className="hero-main">
              <h1>{t('aboutUs.heroTitle')}</h1>
              <p className="hero-subtitle">{t('aboutUs.heroSubtitle')}</p>
              <p className="hero-description">
                {t('aboutUs.heroDescription')}
              </p>
            </div>
            
            <div className="vision-mission-compact">
              <div className="vm-item">
                <div className="vm-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5z"/>
                  </svg>
                </div>
                <h3>Our Vision</h3>
                <p>In a world where time, complexity, and distance often pull us away from sacred traditions,
                   Antaryatra seeks to gently lead us back to our roots. We believe the Sanatan(Hindu) 
                   religious practices are not meant to be difficult, but deeply lived, when guided with clarity, 
                   devotion, and right intent. Carrying the spiritual resonance of Kashi into the rhythm of metro life, 
                   Antaryatra brings timeless rituals into modern homes with grace and authenticity. 
                   Through experienced and well-versed Acharyas, every mantra, method, 
                   and moment is honoured in its true form. Antaryatra is not merely a service, 
                   it is an inward journey, where tradition flows effortlessly into everyday life.</p>
              </div>
              
              <div className="vm-item">
                <div className="vm-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                </div>
                <h3>About Us</h3>
                <p>Antaryatra was born from a deep, lived connection with Kashi and its timeless spiritual traditions. 
                  With roots closely associated with the Brahmin community and its way of life, 
                  the founders bring an authentic understanding of Vedic rituals, acharya lineages, 
                  and the cultural sanctity behind every pooja. At the same time, 
                  Antaryatra recognises the realities of modern life, 
                  where devotion often struggles with time, access, and clarity. 
                  By bridging these worlds, we create a respectful, transparent platform where experienced Kashi-trained Acharyas are empowered, 
                  and individuals and families can engage with Sanatan rituals in their true form—seamlessly, 
                  sincerely, and with trust.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team & Consultants Section */}
      {/* <section className="team-section">
        <div className="container">
          <div className="section-header">
            <h2>{t('aboutUs.teamTitle')}</h2>
            <p>{t('aboutUs.teamSubtitle')}</p>
          </div>
          
          <div className="team-grid">
            {teamMembers.map((member) => (
              <div 
                key={member.id} 
                className="team-member" 
                onClick={() => handleTeamMemberClick(member)}
              >
                <div className="member-image">
                  <img src={member.image} alt={member.name} />
                </div>
                <div className="member-info">
                  <h4>{member.name}</h4>
                  <p className="member-title">{member.title}</p>
                  <p className="member-description">{member.shortDescription}</p>
                  <div className="member-expertise">
                    {member.expertise.slice(0, 2).map((skill, index) => (
                      <span key={index}>{skill}</span>
                    ))}
                  </div>
                  <div className="view-profile-hint">
                    <span>Click to view full profile</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Why Families Trust Us */}
      <section className="trust-section">
        <div className="container">
          <div className="section-header">
            <h2>Why Trust Us</h2>
            <p>Explore our approach to spiritual services</p>
          </div>
          
          <div className="trust-stats">
            <div className="trust-stat">
              <div className="stat-number">Kashi</div>
              <div className="stat-label">Acharya guidance</div>
            </div>
            <div className="trust-stat">
              <div className="stat-number">Vedic</div>
              <div className="stat-label">Ritual traditions</div>
            </div>
            <div className="trust-stat">
              <div className="stat-number">30+</div>
              <div className="stat-label">Structured Pooja & Anusthan Offerings</div>
            </div>
            <div className="trust-stat">
              <div className="stat-number">100%</div>
              <div className="stat-label">Authentic Rituals</div>
            </div>
          </div>

          <div className="trust-features">
            <div className="trust-feature">
              <div className="feature-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <span>{t('aboutUs.trustFeatures.expertPriests')}</span>
            </div>
            <div className="trust-feature">
              <div className="feature-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
              </div>
              <span>{t('aboutUs.trustFeatures.satisfaction')}</span>
            </div>
            <div className="trust-feature">
              <div className="feature-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                </svg>
              </div>
              <span>{t('aboutUs.trustFeatures.support')}</span>
            </div>
            <div className="trust-feature">
              <div className="feature-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                </svg>
              </div>
              <span>Personalized Guidance</span>
            </div>
          </div>
        </div>
      </section>

      {/* Compact Testimonials */}
      {/* <section className="testimonials-section">
        <div className="container">
          <div className="section-header">
            <h2>What Our Community Says</h2>
            <p>Hear from families who have experienced divine transformation</p>
          </div>
          
          <div className="testimonials-carousel">
            <div className="testimonial-container">
              <div className="testimonial-content">
                <div className="stars">★★★★★</div>
                <p>"{testimonials[activeTestimonial].text}"</p>
                <div className="testimonial-author">
                  <img src={testimonials[activeTestimonial].image} alt={testimonials[activeTestimonial].author} />
                  <div className="author-info">
                    <h5>{testimonials[activeTestimonial].author}</h5>
                    <span>{testimonials[activeTestimonial].location}</span>
                  </div>
                </div>
              </div>
              
              <div className="testimonial-nav">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    className={`nav-dot ${index === activeTestimonial ? 'active' : ''}`}
                    onClick={() => setActiveTestimonial(index)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Explore Services Section */}
      <section className="explore-services">
        <div className="container">
          <div className="services-content">
            <div className="services-text">
              <h2>Begin Your Spiritual Journey Today</h2>
              <p>
                Explore our ritual services and speak with our team about your needs.
              </p>
              <div className="services-buttons">
                <button onClick={handleExploreServices} className="btn-primary">Explore Services</button>
                {/* <a href="/booking/consultation/spiritual" className="btn-secondary">Get Consultation</a> */}
              </div>
            </div>
            
            <div className="services-grid">
              <div className="service-card">
                <div className="service-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                </div>
                <h4>Puja Services</h4>
                <p>Traditional ceremonies for every occasion</p>
              </div>
              
              <div className="service-card">
                <div className="service-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                </div>
                <h4>Spiritual Guidance</h4>
                <p>Personalized consultations and advice. Coming soon.</p>
              </div>
              
              {/* <div className="service-card">
                <div className="service-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5z"/>
                  </svg>
                </div>
                <h4>Astrology</h4>
                <p>Vedic astrology readings and remedies</p>
              </div> */}
            </div>
          </div>
        </div>
      </section>

      {/* Team Member Profile Modal */}
      {isModalOpen && selectedTeamMember && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <button className="modal-close" onClick={closeModal}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>
            
            <div className="modal-body">
              <div className="member-profile-header">
                <div className="member-profile-image">
                  <img src={selectedTeamMember.image} alt={selectedTeamMember.name} />
                </div>
                <div className="member-profile-info">
                  <h2>{selectedTeamMember.name}</h2>
                  <h3>{selectedTeamMember.title}</h3>
                  <p className="member-specialization">{selectedTeamMember.specialization}</p>
                </div>
              </div>

              <div className="member-profile-details">
                <div className="profile-section">
                  <h4>About</h4>
                  <p>{selectedTeamMember.fullDescription}</p>
                </div>

                <div className="profile-section">
                  <h4>Education & Training</h4>
                  <p>{selectedTeamMember.education}</p>
                </div>

                <div className="profile-section">
                  <h4>Languages</h4>
                  <div className="languages-list">
                    {selectedTeamMember.languages.map((language: string, index: number) => (
                      <span key={index} className="language-tag">{language}</span>
                    ))}
                  </div>
                </div>

                <div className="profile-section">
                  <h4>Areas of Expertise</h4>
                  <div className="expertise-list">
                    {selectedTeamMember.expertise.map((skill: string, index: number) => (
                      <span key={index} className="expertise-tag">{skill}</span>
                    ))}
                  </div>
                </div>

                <div className="profile-section">
                  <h4>Key Achievements</h4>
                  <ul className="achievements-list">
                    {selectedTeamMember.achievements.map((achievement: string, index: number) => (
                      <li key={index}>{achievement}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="modal-footer">
                <button className="btn-primary" onClick={closeModal}>
                  Book Consultation
                </button>
                <button className="btn-secondary" onClick={closeModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AboutUs;